import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRequest, analyzeRequest } from '../services/api';
import { trackEvent } from '../services/analytics';
import { BATON_ROUGE_METRO } from '../constants/metros';
import { CATEGORY_OPTIONS } from '../constants/categories';
import type { Category } from '../constants/categories';
import { CATEGORY_QUESTIONS, CATEGORY_GUIDANCE } from '../constants/categoryQuestions';
import type { GuidedQuestion } from '../constants/categoryQuestions';

const NewRequest: React.FC = () => {
  const navigate = useNavigate();
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    urgency: '',
    budget_min: '',
    budget_max: '',
    city: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any>(null);
  const [clickedChipIndex, setClickedChipIndex] = useState<number | null>(null);
  const [helperAnswers, setHelperAnswers] = useState<Record<string, string | string[]>>({});
  const [summaryOverride, setSummaryOverride] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/register');
      return;
    }
    if (user?.user_type === 'business') {
      navigate('/requests');
      return;
    }
  }, [user, navigate]);

  // Guided-intake helper questions for the selected category (or _generic).
  const activeQuestions = CATEGORY_QUESTIONS[formData.category as Category] ?? CATEGORY_QUESTIONS._generic;

  // Shared compose helper — used by BOTH the preview and submit. Empty when no
  // optional answers are filled in, so the description stays byte-for-byte equal.
  const jobDetailLines = activeQuestions
    .map(q => {
      const v = helperAnswers[q.id];
      const val = Array.isArray(v) ? v.join(', ') : (typeof v === 'string' ? v.trim() : '');
      return val ? `- ${q.label}: ${val}` : null;
    })
    .filter(Boolean) as string[];

  const setHelperValue = (qid: string, value: string) => {
    setHelperAnswers(prev => ({ ...prev, [qid]: value }));
  };

  const toggleChip = (qid: string, option: string) => {
    setHelperAnswers(prev => {
      const current = Array.isArray(prev[qid]) ? (prev[qid] as string[]) : [];
      const next = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      return { ...prev, [qid]: next };
    });
  };

  const renderHelperInput = (q: GuidedQuestion) => {
    if (q.type === 'chips') {
      const selected = Array.isArray(helperAnswers[q.id]) ? (helperAnswers[q.id] as string[]) : [];
      return (
        <div style={styles.chipRow}>
          {q.options?.map(option => {
            const isSel = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggleChip(q.id, option)}
                aria-pressed={isSel}
                style={{ ...styles.chip, ...(isSel ? styles.chipSelected : {}) }}
              >
                {option}
              </button>
            );
          })}
        </div>
      );
    }
    if (q.type === 'select') {
      const value = typeof helperAnswers[q.id] === 'string' ? (helperAnswers[q.id] as string) : '';
      return (
        <select
          value={value}
          onChange={e => setHelperValue(q.id, e.target.value)}
          style={styles.select}
        >
          <option value="">-- Select (optional) --</option>
          {q.options?.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    const value = typeof helperAnswers[q.id] === 'string' ? (helperAnswers[q.id] as string) : '';
    return (
      <input
        type="text"
        value={value}
        onChange={e => setHelperValue(q.id, e.target.value)}
        placeholder={q.placeholder}
        style={styles.input}
      />
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'title' || e.target.name === 'description') {
      setAiSuggestions(null);
    }
    if (e.target.name === 'category') {
      setClickedChipIndex(null);
      setHelperAnswers({});
      setSummaryOverride(null);
    }
  };

  const handleAIAnalyze = async () => {
    if (!formData.description) {
      alert('Please enter a description first');
      return;
    }
    setAnalyzing(true);
    try {
      const response = await analyzeRequest({
        title: formData.title,
        description: formData.description,
      });
      setAiSuggestions(response.data.analysis);
      setClickedChipIndex(null);
      if (response.data.analysis.category) {
        setFormData(prev => ({ ...prev, category: prev.category || response.data.analysis.category }));
      }
    } catch (err) {
      console.error('AI analysis failed:', err);
      alert('AI analysis failed. You can still submit manually.');
    } finally {
      setAnalyzing(false);
    }
  };

  const applySuggestedTitle = () => {
    if (aiSuggestions?.suggestedTitle) {
      setFormData(prev => ({ ...prev, title: aiSuggestions.suggestedTitle }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Budget is optional. Blank/blank is valid; anything else must be a
    // complete, non-negative, correctly-ordered range.
    const minRaw = formData.budget_min.trim();
    const maxRaw = formData.budget_max.trim();
    const budgetOmitted = minRaw === '' && maxRaw === '';

    if (!budgetOmitted) {
      if (minRaw === '' || maxRaw === '') {
        setError('Please provide both a minimum and a maximum budget, or leave both blank.');
        return;
      }
      const budgetMin = Number(minRaw);
      const budgetMax = Number(maxRaw);
      if (
        !Number.isFinite(budgetMin) || !Number.isFinite(budgetMax) ||
        budgetMin < 0 || budgetMax < 0 ||
        budgetMin > budgetMax
      ) {
        setError('Please enter a valid budget range. The minimum cannot be higher than the maximum.');
        return;
      }
    }

    const detailBlock = jobDetailLines.length
      ? (summaryOverride ?? jobDetailLines.join('\n'))
      : '';
    const finalDescription = detailBlock.trim()
      ? `${formData.description}\n\nJob details:\n${detailBlock}`
      : formData.description;

    setLoading(true);
    setError('');
    try {
      await createRequest({
        ...formData,
        description: finalDescription,
        user_id: user.id,
        budget_min: budgetOmitted ? null : Number(minRaw),
        budget_max: budgetOmitted ? null : Number(maxRaw),
      });
      trackEvent('Lead', { content_category: formData.category });
      navigate('/my-requests');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.form}>
        <h2> Post a Quote Request</h2>
        <p style={styles.subtitle}>Tell businesses what you need and let them compete for your business!</p>

        {error && (
          <div style={styles.errorBox}>
            {error.split('\n').map((line, i) => (
              <p key={i} style={{ margin: '4px 0' }}>{line}</p>
            ))}
            <button type="button" onClick={() => setError('')} style={styles.errorClose}>
               Dismiss
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label>What do you need?</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              style={styles.input}
              placeholder="e.g., Roof replacement for 2,000 sq ft home"
            />
            {aiSuggestions?.suggestedTitle && aiSuggestions.suggestedTitle !== formData.title && (
              <div style={styles.suggestion}>
                 AI Suggestion: <strong>{aiSuggestions.suggestedTitle}</strong>
                <button type="button" onClick={applySuggestedTitle} style={styles.applyButton}>Apply</button>
              </div>
            )}
          </div>

          <div style={styles.field}>
            <label>Describe in detail</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              style={styles.textarea}
              placeholder="Include any specifics - size, condition, brand preferences, etc."
            />
            <button
              type="button"
              onClick={handleAIAnalyze}
              disabled={analyzing || !formData.description}
              style={styles.aiButton}
            >
              {analyzing ? ' Analyzing...' : ' AI Analyze'}
            </button>
          </div>

          {aiSuggestions && (
            <div style={styles.aiResults}>
              <h4> AI Analysis</h4>
              {aiSuggestions.extractedDetails && Object.keys(aiSuggestions.extractedDetails).length > 0 && (
                <div style={styles.details}>
                  <strong>Extracted Details:</strong>
                  <ul>
                    {aiSuggestions.extractedDetails.brand && <li>Brand: {aiSuggestions.extractedDetails.brand}</li>}
                    {aiSuggestions.extractedDetails.year && <li>Year: {aiSuggestions.extractedDetails.year}</li>}
                    {aiSuggestions.extractedDetails.model && <li>Model: {aiSuggestions.extractedDetails.model}</li>}
                    {aiSuggestions.extractedDetails.condition && <li>Condition: {aiSuggestions.extractedDetails.condition}</li>}
                    {aiSuggestions.extractedDetails.quantity && <li>Quantity: {aiSuggestions.extractedDetails.quantity}</li>}
                  </ul>
                </div>
              )}
              {aiSuggestions.clarifyingQuestions?.length > 0 && (
                <div style={styles.questions}>
                  <strong>Consider adding:</strong>
                  <ul>
                    {aiSuggestions.clarifyingQuestions.map((q: string, i: number) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {aiSuggestions.tags?.length > 0 && (
                <div style={styles.tags}>
                  {aiSuggestions.tags.map((tag: string, i: number) => {
                    const slug = aiSuggestions.tagSlugs?.[i];
                    const isClickable = !!slug && (CATEGORY_OPTIONS as readonly string[]).includes(slug);
                    const isSelected = isClickable && clickedChipIndex === i;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={isClickable ? () => {
                          setFormData(prev => ({ ...prev, category: slug }));
                          setClickedChipIndex(i);
                        } : undefined}
                        aria-pressed={isClickable ? isSelected : undefined}
                        aria-disabled={!isClickable ? true : undefined}
                        style={{
                          ...styles.tag,
                          cursor: isClickable ? 'pointer' : 'default',
                          ...(isSelected ? styles.tagSelected : {}),
                        }}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div style={styles.row}>
            <div style={styles.field}>
              <label>Category {aiSuggestions?.category && ' AI Selected'}</label>
              <select name="category" value={formData.category} onChange={handleChange} style={styles.select} required>
                <option value="">-- Select a Category --</option>
                <optgroup label="Automotive">
                  <option value="auto_repair"> Auto Repair &amp; Maintenance</option>
                  <option value="auto_body"> Auto Body &amp; Paint</option>
                  <option value="auto_parts"> Auto Parts</option>
                  <option value="transmission"> Transmission &amp; Drivetrain</option>
                  <option value="oil_change"> Oil Change</option>
                  <option value="tire_service"> Tire Service</option>
                  <option value="towing"> Towing &amp; Recovery</option>
                </optgroup>
                <optgroup label="Home Services">
                  <option value="plumbing"> Plumbing</option>
                  <option value="electrical"> Electrical</option>
                  <option value="hvac"> HVAC &amp; Air Conditioning</option>
                  <option value="roofing"> Roofing</option>
                  <option value="gutters"> Gutters</option>
                  <option value="siding"> Siding</option>
                  <option value="awnings"> Awnings</option>
                  <option value="painting"> Painting</option>
                  <option value="flooring"> Flooring</option>
                  <option value="remodeling"> Remodeling &amp; Construction</option>
                  <option value="concrete"> Concrete</option>
                  <option value="patios"> Patios / Patio Covers</option>
                  <option value="general_contractor"> General Contractor</option>
                  <option value="appliance_repair"> Appliance Repair</option>
                  <option value="handyman"> Handyman</option>
                  <option value="windows_doors"> Windows &amp; Doors</option>
                  <option value="fence_deck"> Fence &amp; Deck</option>
                </optgroup>
                <optgroup label="Outdoor &amp; Property">
                  <option value="landscaping"> Landscaping &amp; Lawn Care</option>
                  <option value="lawn_care"> Lawn Care</option>
                  <option value="fencing"> Fencing</option>
                  <option value="pressure_washing"> Pressure Washing</option>
                  <option value="tree_service"> Tree Service</option>
                  <option value="pool_service"> Pool Service</option>
                </optgroup>
                <optgroup label="Cleaning &amp; Maintenance">
                  <option value="house_cleaning"> House Cleaning</option>
                  <option value="carpet_cleaning"> Carpet &amp; Upholstery Cleaning</option>
                  <option value="pest_control"> Pest Control</option>
                  <option value="junk_removal"> Junk Removal</option>
                  <option value="mold_remediation"> Mold Removal / Remediation</option>
                  <option value="water_damage_restoration"> Water Damage Restoration</option>
                </optgroup>
                <optgroup label="Pet Services">
                  <option value="dog_training"> Dog Training</option>
                  <option value="pet_grooming"> Pet Grooming</option>
                  <option value="pet_sitting"> Pet Sitting &amp; Walking</option>
                  <option value="pet_boarding"> Pet Boarding</option>
                  <option value="veterinary"> Veterinary</option>
                </optgroup>
                <optgroup label="Professional Services">
                  <option value="legal"> Legal</option>
                  <option value="accounting"> Accounting &amp; Tax</option>
                  <option value="insurance"> Insurance</option>
                  <option value="real_estate"> Real Estate</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="electronics"> Electronics &amp; IT</option>
                  <option value="moving"> Moving &amp; Storage</option>
                  <option value="catering"> Catering &amp; Events</option>
                  <option value="photography"> Photography &amp; Video</option>
                  <option value="tutoring"> Tutoring &amp; Education</option>
                  <option value="other"> Other</option>
                </optgroup>
              </select>
            </div>

            <div style={styles.field}>
              <label>When are you looking to hire?</label>
              <select name="urgency" value={formData.urgency} onChange={handleChange} required style={styles.select}>
                <option value="">Select one...</option>
                <option value="ready_now">Ready to hire now</option>
                <option value="within_30_days">Within 30 days</option>
                <option value="comparing">Comparing options</option>
                <option value="researching">Researching only</option>
              </select>
            </div>
          </div>

          {formData.category && (
            <div style={styles.helperSection}>
              <div style={styles.helperHeader}>
                <span style={styles.helperTitle}>Help contractors understand the job</span>
                <p style={styles.helperSubtext}>
                  Answer a few optional questions so contractors can give a more accurate quote.
                </p>
                {CATEGORY_GUIDANCE[formData.category as Category] && (
                  <p style={styles.helperQuestionNote}>
                    {CATEGORY_GUIDANCE[formData.category as Category]}
                  </p>
                )}
              </div>
              {activeQuestions.map(q => (
                <div key={q.id} style={styles.field}>
                  <label>{q.label}</label>
                  {renderHelperInput(q)}
                  {q.helperText && <p style={styles.helperQuestionNote}>{q.helperText}</p>}
                </div>
              ))}
            </div>
          )}

          {jobDetailLines.length > 0 && (
            <div style={styles.previewCard}>
              <p style={styles.previewLabel}>Contractor-ready summary</p>
              <p style={styles.helperSubtext}>You can edit this before submitting.</p>
              <div style={styles.previewBody}>
                <strong>Job details:</strong>
                <textarea
                  value={summaryOverride ?? jobDetailLines.join('\n')}
                  onChange={e => setSummaryOverride(e.target.value)}
                  style={styles.summaryTextarea}
                  rows={8}
                  aria-label="Job details summary"
                />
                {summaryOverride !== null && (
                  <button type="button" onClick={() => setSummaryOverride(null)} style={styles.resetSummaryBtn}>
                    Reset to generated
                  </button>
                )}
              </div>
            </div>
          )}

          <div style={styles.row}>
            <div style={styles.field}>
              <label>Budget Min ($)</label>
              <input
                type="number"
                name="budget_min"
                value={formData.budget_min}
                onChange={handleChange}
                style={styles.input}
                placeholder="100"
              />
            </div>
            <div style={styles.field}>
              <label>Budget Max ($)</label>
              <input
                type="number"
                name="budget_max"
                value={formData.budget_max}
                onChange={handleChange}
                style={styles.input}
                placeholder="500"
              />
            </div>
          </div>
          <p style={styles.helperQuestionNote}>
            Optional. If you're not sure what this should cost, leave it blank &mdash; contractors will quote based on the job details.
          </p>

          <div style={styles.field}>
            <label>Phone Number <span style={{fontSize:"12px",color:"#999",fontWeight:400}}>(Optional — lets contractors call or text with questions)</span></label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={styles.input}
              placeholder="(225) 000-0000"
            />
          </div>

          <div style={styles.field}>
            <label>City <span style={{ color: '#e74c3c' }}>*</span></label>
            <select
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              style={styles.select}
            >
              <option value="">-- Select your city --</option>
              {BATON_ROUGE_METRO.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Posting...' : ' Post Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', justifyContent: 'center', padding: '40px 20px' },
  form: { backgroundColor: 'white', padding: '40px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', width: '100%', maxWidth: '600px' },
  subtitle: { color: '#7f8c8d', marginBottom: '30px' },
  field: { marginBottom: '20px', flex: 1 },
  row: { display: 'flex', gap: '20px' },
  input: { width: '100%', padding: '12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '5px', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '5px', minHeight: '120px', boxSizing: 'border-box' },
  select: { width: '100%', padding: '12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '5px', marginTop: '5px', boxSizing: 'border-box' },
  button: { width: '100%', padding: '16px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '5px', fontSize: '18px', cursor: 'pointer', marginTop: '10px' },
  aiButton: { marginTop: '10px', padding: '10px 20px', backgroundColor: '#9b59b6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' },
  aiResults: { backgroundColor: '#f8f4ff', border: '1px solid #9b59b6', borderRadius: '10px', padding: '20px', marginBottom: '20px' },
  details: { marginBottom: '15px' },
  questions: { marginBottom: '15px', color: '#e67e22' },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  tag: { backgroundColor: '#9b59b6', color: 'white', padding: '4px 10px', borderRadius: '15px', fontSize: '12px', border: 'none', fontFamily: 'inherit', lineHeight: 1.4 },
  tagSelected: { backgroundColor: '#7d3c98', fontWeight: 700, boxShadow: '0 0 0 2px #10b981' },
  suggestion: { marginTop: '10px', padding: '10px', backgroundColor: '#fff9e6', borderRadius: '5px', fontSize: '14px' },
  applyButton: { marginLeft: '10px', padding: '5px 10px', backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' },
  error: { color: '#e74c3c', backgroundColor: '#fdeaea', padding: '10px', borderRadius: '5px', marginBottom: '20px' },
  errorBox: { color: '#c53030', backgroundColor: '#fff5f5', border: '1px solid #fc8181', padding: '16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', lineHeight: 1.6 },
  errorClose: { marginTop: '10px', padding: '6px 14px', background: '#c53030', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' },
  helperSection: { background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' },
  helperHeader: { marginBottom: '12px' },
  helperTitle: { fontSize: '14px', fontWeight: 600, color: '#2b6cb0', display: 'block' },
  helperSubtext: { fontSize: '12px', color: '#666', margin: '4px 0 0' },
  helperQuestionNote: { fontSize: '12px', color: '#666', margin: '4px 0 0' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px' },
  chip: { background: '#fff', color: '#2b6cb0', padding: '8px 14px', borderRadius: '15px', fontSize: '13px', border: '1px solid #bee3f8', cursor: 'pointer', fontFamily: 'inherit' },
  chipSelected: { background: '#2b6cb0', color: '#fff', fontWeight: 600, borderColor: '#2b6cb0' },
  previewCard: { background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' },
  previewLabel: { fontSize: '13px', fontWeight: 600, color: '#2b6cb0', margin: '0 0 8px' },
  previewBody: { fontSize: '13px', color: '#1a202c', lineHeight: 1.6 },
  summaryTextarea: { width: '100%', padding: '10px', fontSize: '13px', color: '#1a202c', lineHeight: 1.6, border: '1px solid #bee3f8', borderRadius: '8px', marginTop: '6px', minHeight: '90px', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' },
  resetSummaryBtn: { marginTop: '8px', padding: '4px 12px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' },
};

export default NewRequest;